// Функции расчёта потери напряжения и выбора сечения кабеля

import type { 
  Material, 
  LineType, 
  NetworkType, 
  CalculationInput, 
  VoltageLossResult 
} from '../types';
import { 
  RESISTIVITY, 
  TEMP_COEFFICIENT, 
  MAX_VOLTAGE_LOSS,
  X0_OVERHEAD,
} from '../data/constants';
import { X0_BY_SECTION } from '../data/tables';

/**
 * Вычисление sin(φ) по cos(φ)
 * sin²φ + cos²φ = 1 => sinφ = √(1 - cos²φ)
 */
export function calculateSinPhi(cosPhi: number): number {
  // Ограничиваем cosPhi в диапазоне [0, 1]
  const clampedCosPhi = Math.max(0, Math.min(1, cosPhi));
  return Math.sqrt(1 - clampedCosPhi * clampedCosPhi);
}

/**
 * Вычисление температурного коэффициента
 * kt = 1 + α × (Tраб - 20)
 */
export function calculateTempCoefficient(
  material: Material,
  workingTemp: number
): number {
  const alpha = TEMP_COEFFICIENT[material];
  return 1 + alpha * (workingTemp - 20);
}

/**
 * Расчёт активного сопротивления линии
 * R = ρ₂₀ × (L / S) × kt
 * @param material - материал проводника
 * @param length - длина линии, м
 * @param crossSection - сечение, мм²
 * @param workingTemp - рабочая температура, °C (обычно 20 для потери U)
 * @returns активное сопротивление, Ом
 */
export function calculateResistance(
  material: Material,
  length: number,
  crossSection: number,
  workingTemp: number
): number {
  const rho20 = RESISTIVITY[material];
  const kt = calculateTempCoefficient(material, workingTemp);
  return rho20 * (length / crossSection) * kt;
}

/**
 * Расчёт реактивного сопротивления линии
 * X = X₀ × Lкм
 * @param crossSection - сечение, мм²
 * @param length - длина линии, м
 * @param lineType - тип линии (кабельная или воздушная)
 * @returns реактивное сопротивление, Ом
 */
export function calculateReactance(
  crossSection: number,
  length: number,
  lineType: LineType
): number {
  const lengthKm = length / 1000; // Перевод в км
  
  if (lineType === 'overhead') {
    return X0_OVERHEAD * lengthKm;
  }
  
  // Для кабельных линий берём X₀ из таблицы по сечению
  // Если сечение не найдено, берём ближайшее
  const sections = Object.keys(X0_BY_SECTION).map(Number).sort((a, b) => a - b);
  let nearestSection = sections[0];
  
  for (const section of sections) {
    if (section <= crossSection) {
      nearestSection = section;
    } else {
      break;
    }
  }
  
  const x0 = X0_BY_SECTION[nearestSection] ?? 0.08; // Значение по умолчанию
  return x0 * lengthKm;
}

/**
 * Расчёт полного сопротивления
 * Z = √(R² + X²)
 */
export function calculateImpedance(resistance: number, reactance: number): number {
  return Math.sqrt(resistance * resistance + reactance * reactance);
}

/**
 * Пересчёт мощности в ток
 * Для трёхфазной сети: I = P / (√3 × U × cosφ)
 * Для однофазной сети: I = P / (U × cosφ)
 * @param power - мощность, кВт
 * @param voltage - напряжение, В
 * @param cosPhi - коэффициент мощности
 * @param networkType - тип сети
 * @returns ток, А
 */
export function powerToCurrent(
  power: number,
  voltage: number,
  cosPhi: number,
  networkType: NetworkType
): number {
  const powerW = power * 1000; // Перевод кВт в Вт
  
  if (networkType === 'three-phase') {
    return powerW / (Math.sqrt(3) * voltage * cosPhi);
  } else {
    return powerW / (voltage * cosPhi);
  }
}

/**
 * Расчёт потери напряжения (основная функция)
 * Трёхфазная сеть: ΔU = √3 × I × (R × cosφ + X × sinφ)
 * Однофазная сеть: ΔU = 2 × I × (R × cosφ + X × sinφ)
 * 
 * @param input - входные данные для расчёта
 * @returns результаты расчёта потери напряжения
 */
export function calculateVoltageLoss(input: CalculationInput): VoltageLossResult {
  const {
    nominalVoltage,
    current,
    power,
    length,
    crossSection,
    powerFactor,
    networkType,
    material,
    loadType,
    lineType,
  } = input;

  // Определяем ток: если задана мощность, пересчитываем, иначе используем заданный ток
  let calculatedCurrent = current;
  if (power && power > 0) {
    calculatedCurrent = powerToCurrent(power, nominalVoltage, powerFactor, networkType);
  }

  // Вычисляем сопротивления (при температуре 20°C для расчёта потери напряжения)
  const resistance = calculateResistance(material, length, crossSection, 20);
  const reactance = calculateReactance(crossSection, length, lineType);
  const impedance = calculateImpedance(resistance, reactance);

  // Вычисляем sin(φ)
  const sinPhi = calculateSinPhi(powerFactor);

  // Вычисляем потерю напряжения
  const baseLoss = resistance * powerFactor + reactance * sinPhi;
  let voltageLoss: number;
  
  if (networkType === 'three-phase') {
    voltageLoss = Math.sqrt(3) * calculatedCurrent * baseLoss;
  } else {
    voltageLoss = 2 * calculatedCurrent * baseLoss;
  }

  // Вычисляем потерю в процентах
  const voltageLossPercent = (voltageLoss / nominalVoltage) * 100;

  // Определяем допустимую потерю
  const maxAllowedLoss = MAX_VOLTAGE_LOSS[loadType];

  // Проверяем, укладываемся ли в норму
  const isWithinNorm = voltageLossPercent <= maxAllowedLoss;

  return {
    voltageLoss: roundTo(voltageLoss, 2),
    voltageLossPercent: roundTo(voltageLossPercent, 2),
    resistance: roundTo(resistance, 6),
    reactance: roundTo(reactance, 4),
    impedance: roundTo(impedance, 6),
    isWithinNorm,
    maxAllowedLoss,
    calculatedCurrent: roundTo(calculatedCurrent, 2),
  };
}

/**
 * Округление числа до указанного количества знаков после запятой
 */
function roundTo(value: number, decimals: number): number {
  const multiplier = Math.pow(10, decimals);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * Подбор минимального сечения по допустимой потере напряжения
 * @param input - входные данные (сечение игнорируется)
 * @param sections - массив стандартных сечений для подбора
 * @returns минимальное подходящее сечение или null, если не найдено
 */
export function selectSectionByVoltageLoss(
  input: CalculationInput,
  sections: readonly number[]
): number | null {
  // Ищем минимальное сечение, которое удовлетворяет требованиям
  for (const section of sections) {
    const testInput = { ...input, crossSection: section };
    const result = calculateVoltageLoss(testInput);
    
    if (result.isWithinNorm) {
      return section;
    }
  }
  
  // Если не нашли подходящее сечение среди стандартных
  return null;
}

// Экспорт устаревшей функции для обратной совместимости
export function calculateVoltageDrop(
  length: number,
  current: number,
  section: number,
  resistivity: number
): number {
  // Базовая формула: ΔU = (2 * ρ * L * I) / S
  return (2 * resistivity * length * current) / section;
}

// Заглушка для будущей реализации
export function selectCableSection(
  current: number,
  length: number,
  material: 'copper' | 'aluminum'
): number {
  // Параметры зарезервированы для будущей реализации
  void current;
  void length;
  void material;
  return 0;
}
